import {
    NotificationDuration
} from '@js/components/modules/constants';

const Notification = (function () {
    const self = {
        notificationElement: document.getElementById('notification-element'),
        notificationContent: document.getElementById('notification-content'),
        infoIcon: document.getElementById('notification-info'),
        successIcon: document.getElementById('notification-success'),
        warningIcon: document.getElementById('notification-warning'),
        errorIcon: document.getElementById('notification-error'),
        notificationLevels: ['info', 'success', 'warning', 'error'],
        notificationDuration: NotificationDuration,
        notificationAnimationDuration: 300,
        notificationAnimationClass: 'duration-300'
    };

    const _checkInitialNotification = function () {
        const flashes = document.querySelectorAll('.flash-message');

        Array.prototype.forEach.call(flashes, function (element) {
            const level = element.getAttribute('data-level');
            const token = element.getAttribute('data-flash-token');

            if (sessionStorage) {
                // Do not display same flash message twice
                if (sessionStorage.getItem(`flash-message-${token}`)) {
                    return;
                }
            }

            // Lets the Notification component initialize
            document.addEventListener('DOMContentLoaded', function () {
                setTimeout(function () {
                    if (level === 'success') {
                        self.success(element.innerHTML);
                    } else if (level === 'error') {
                        self.error(element.innerHTML);
                    } else {
                        self.info(element.innerHTML);
                    }
                }, 200);
            });

            if (sessionStorage) {
                sessionStorage.setItem(`flash-message-${token}`, 'true');
            }
        });
    };

    const _adaptToLevel = function (level) {
        self.notificationLevels.forEach((l) => self.notificationElement.classList.remove(`notification-${l}`));

        self.notificationElement.classList.add(`notification-${level}`);
    };

    const _showIcon = function (level) {
        self.notificationLevels.forEach((l) => {
            self[`${l}Icon`].classList.add('hidden');
            self[`${l}Icon`].classList.remove('inline-flex');
        });

        self[`${level}Icon`].classList.remove('hidden');
    };

    const _setContent = function (content) {
        self.notificationContent.innerHTML = content;
    };

    const _hideNotification = function () {
        // Stop catching clicks as soon as it starts fading: a transparent toast at
        // z-notification (above modals) would otherwise silently block the UI below it.
        self.notificationElement.classList.add('pointer-events-none');
        self.notificationElement.classList.add('transition-opacity', self.notificationAnimationClass, 'ease-out');
        self.notificationElement.classList.add('opacity-0');

        setTimeout(() => self.notificationElement.classList.remove('opacity-100'), 1);

        setTimeout(() => self.notificationElement.classList.add('hidden'), self.notificationAnimationDuration);
    };

    const _showNotification = function (autoHide = true) {
        // Reset any inline styles left over from a previous swipe gesture
        self.notificationElement.style.transition = '';
        self.notificationElement.style.transform = '';
        self.notificationElement.style.opacity = '';

        self.notificationElement.classList.remove('hidden');
        // Only interactive (close button / swipe) while actually shown
        self.notificationElement.classList.remove('pointer-events-none');
        self.notificationElement.classList.add('inline-flex');
        self.notificationElement.classList.add('transition-opacity', self.notificationAnimationClass, 'ease-out');
        self.notificationElement.classList.add('opacity-0');

        setTimeout(() => self.notificationElement.classList.add('opacity-100'), 1);

        if (autoHide) {
            setTimeout(() => _hideNotification(), self.notificationDuration);
        }
    };

    const _swipeOut = function (direction) {
        const el = self.notificationElement;

        el.classList.add('pointer-events-none');
        el.style.transition = 'transform 200ms ease-out, opacity 200ms ease-out';
        el.style.transform = `translateX(${direction * el.offsetWidth * 1.2}px)`;
        el.style.opacity = '0';

        setTimeout(() => {
            el.classList.add('hidden');
            el.classList.remove('inline-flex');
            el.style.transition = '';
            el.style.transform = '';
            el.style.opacity = '';
        }, 220);
    };

    const _bindCloseButton = function () {
        const closeButton = self.notificationElement.querySelector('[data-dismiss-target]');

        if (!closeButton) {
            return;
        }

        closeButton.addEventListener('click', (event) => {
            event.preventDefault();
            _hideNotification();
        });
    };

    const _bindSwipe = function () {
        const el = self.notificationElement;

        let startX = 0;
        let startY = 0;
        let deltaX = 0;
        let width = 0;
        let dragging = false;
        let horizontal = false;

        el.addEventListener('touchstart', (event) => {
            if (event.touches.length !== 1) {
                return;
            }

            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            deltaX = 0;
            width = el.offsetWidth;
            dragging = true;
            horizontal = false;

            // Follow the finger without any easing
            el.style.transition = 'none';
        }, {passive: true});

        el.addEventListener('touchmove', (event) => {
            if (!dragging) {
                return;
            }

            const x = event.touches[0].clientX - startX;
            const y = event.touches[0].clientY - startY;

            // Lock the gesture to horizontal, or bail out if it's a vertical scroll
            if (!horizontal) {
                if (Math.abs(x) < 10 && Math.abs(y) < 10) {
                    return;
                }

                if (Math.abs(y) > Math.abs(x)) {
                    dragging = false;
                    el.style.transition = '';

                    return;
                }

                horizontal = true;
            }

            deltaX = x;
            el.style.transform = `translateX(${deltaX}px)`;
            el.style.opacity = String(Math.max(0, 1 - Math.abs(deltaX) / width));
            event.preventDefault();
        }, {passive: false});

        const onTouchEnd = () => {
            if (!dragging) {
                return;
            }

            dragging = false;

            if (Math.abs(deltaX) > width * 0.35) {
                _swipeOut(deltaX > 0 ? 1 : -1);

                return;
            }

            // Snap back into place
            el.style.transition = 'transform 200ms ease-out, opacity 200ms ease-out';
            el.style.transform = '';
            el.style.opacity = '';

            setTimeout(() => {
                el.style.transition = '';
            }, 220);
        };

        el.addEventListener('touchend', onTouchEnd);
        el.addEventListener('touchcancel', onTouchEnd);
    };

    /* Initialization
    ******************** */
    self.initialize = function () {
        if (self.notificationElement) {
            _bindCloseButton();
            _bindSwipe();
        }

        _checkInitialNotification();
    };

    self.notificationLevels.forEach((level) => {
        self[level] = (content, autoHide = true) => {
            if (!self.notificationElement) {
                return;
            }

            _adaptToLevel(level);

            _showIcon(level);

            _setContent(content);

            _showNotification(autoHide);
        };
    });

    self.hide = function () {
        _hideNotification();
    };

    return self;
})();

export default Notification;
